import * as Sentry from '@sentry/react-router'

/**
 * Client-side logger with automatic Sentry integration.
 *
 * Mirrors the server logger API for consistency across the codebase.
 *
 * @example
 * ```tsx
 * // Log an error (automatically captured in Sentry)
 * logger.error('Failed to authenticate user', error, {
 *   component: 'LoginForm',
 *   tags: { feature: 'webauthn' }
 * })
 *
 * // Log a warning (not captured in Sentry by default)
 * logger.warn('Account not found', { accountId }, {
 *   captureInSentry: false
 * })
 *
 * // Log info for debugging (not captured in Sentry)
 * logger.info('User completed signup', { userId })
 * ```
 */

type SentryLevel = 'fatal' | 'error' | 'warning' | 'info' | 'log'
type ConsoleLevel = 'error' | 'warn' | 'info'

interface LogOptions {
	/** Additional context to include with the log */
	context?: Record<string, unknown>
	/** Tags to categorize in Sentry */
	tags?: Record<string, string>
	/** Whether to capture in Sentry (default: true for errors, false for info/warn) */
	captureInSentry?: boolean
	/** Component name for automatic context */
	component?: string
}

function getSentrySeverity(level: ConsoleLevel): SentryLevel {
	const severityMap: Record<ConsoleLevel, SentryLevel> = {
		error: 'error',
		warn: 'warning',
		info: 'info',
	}
	return severityMap[level]
}

function createLogger(defaultLevel: ConsoleLevel) {
	return (
		message: string,
		errorOrContext?: Error | Record<string, unknown>,
		options?: LogOptions,
	) => {
		const isError = errorOrContext instanceof Error
		const error = isError ? errorOrContext : undefined
		const contextData = isError ? options?.context : errorOrContext

		// Determine if we should capture in Sentry
		const shouldCapture = options?.captureInSentry ?? defaultLevel === 'error'

		// Always log to console for development visibility
		if (error) {
			console[defaultLevel](message, error, contextData)
		} else if (contextData) {
			console[defaultLevel](message, contextData)
		} else {
			console[defaultLevel](message)
		}

		// Capture in Sentry if appropriate
		if (shouldCapture) {
			const sentrySeverity = getSentrySeverity(defaultLevel)

			// Enrich context with automatic metadata
			const enrichedContext = {
				...contextData,
				...(options?.component && { component: options.component }),
				url: typeof window !== 'undefined' ? window.location.href : undefined,
				timestamp: new Date().toISOString(),
			}

			if (error) {
				Sentry.captureException(error, {
					level: sentrySeverity,
					tags: options?.tags,
					extra: { message, ...enrichedContext },
				})
			} else {
				Sentry.captureMessage(message, {
					level: sentrySeverity,
					tags: options?.tags,
					extra: enrichedContext,
				})
			}
		}
	}
}

export const logger = {
	/**
	 * Log an error and report it to Sentry.
	 *
	 * Use for unexpected errors that should be investigated.
	 *
	 * @param message - Human-readable error message
	 * @param errorOrContext - Error object or additional context
	 * @param options - Logging options (tags, component, etc.)
	 *
	 * @example
	 * ```tsx
	 * try {
	 *   await authenticateUser()
	 * } catch (error) {
	 *   logger.error('Authentication failed', error, {
	 *     component: 'LoginForm',
	 *     tags: { feature: 'auth' }
	 *   })
	 * }
	 * ```
	 */
	error: createLogger('error'),

	/**
	 * Log a warning. Only sent to Sentry if explicitly enabled.
	 *
	 * Use for recoverable issues or degraded functionality.
	 *
	 * @param message - Human-readable warning message
	 * @param errorOrContext - Error object or additional context
	 * @param options - Logging options (set captureInSentry: true to report)
	 *
	 * @example
	 * ```tsx
	 * logger.warn('Using fallback configuration', { config }, {
	 *   captureInSentry: false
	 * })
	 * ```
	 */
	warn: createLogger('warn'),

	/**
	 * Log informational message. Not sent to Sentry by default.
	 *
	 * Use for debugging or operational visibility.
	 *
	 * @param message - Human-readable info message
	 * @param context - Additional context data
	 * @param options - Logging options (set captureInSentry: true to report)
	 *
	 * @example
	 * ```tsx
	 * logger.info('User completed onboarding', { userId })
	 * ```
	 */
	info: createLogger('info'),
}
